"use client";

import { motion, useReducedMotion } from "motion/react";
import Image from "next/image";

import { REVEAL_STAGGER, TextReveal } from "@/components/elements/text-reveal";
import { IconApple } from "@/components/icons/apple";
import { IconGooglePlay } from "@/components/icons/google-play";
import { StoreBadge } from "@/components/ui/store-badge";
import { appStoreUrl, SHOW_ANDROID, STORE_LINKS } from "@/lib/constants";
import { cn } from "@/lib/utils";

const FLOATING_CARDS = [
  {
    label: "🌅 Golden hour",
    src: "/images/hero/cards/golden-hour.jpg",
    position: "left-[6%] top-[18%]",
    rotate: -4,
    loop: { duration: 4.8, delay: 1.2 },
    entrance: { delay: 0.5 },
  },
  {
    label: "🌆 City lights",
    src: "/images/hero/cards/city-lights.jpg",
    position: "right-[6%] top-[34%]",
    rotate: 4,
    loop: { duration: 5.4, delay: 1.5 },
    entrance: { delay: 0.7 },
  },
];

export function Cta() {
  const reduced = useReducedMotion();

  return (
    <section className="relative overflow-hidden pt-32 pb-24">
      {/* Floating photo cards */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden lg:block"
      >
        {FLOATING_CARDS.map((card) => (
          <motion.div
            key={card.label}
            className={cn("absolute", card.position)}
            initial={
              reduced ? { opacity: 0 } : { opacity: 0, y: 32, scale: 0.9 }
            }
            whileInView={
              reduced
                ? { opacity: 1, rotate: card.rotate }
                : {
                    opacity: 1,
                    scale: 1,
                    y: [0, -10, 0],
                    rotate: [card.rotate, card.rotate + 2, card.rotate],
                  }
            }
            viewport={{ once: true, amount: 0.4 }}
            transition={
              reduced
                ? { duration: 0.6, delay: card.entrance.delay }
                : {
                    opacity: { duration: 0.6, delay: card.entrance.delay },
                    scale: {
                      duration: 0.8,
                      delay: card.entrance.delay,
                      ease: "backOut",
                    },
                    y: {
                      duration: card.loop.duration,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: card.loop.delay,
                    },
                    rotate: {
                      duration: card.loop.duration,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: card.loop.delay,
                    },
                  }
            }
          >
            <div className="relative w-[180px]">
              <Image
                src={card.src}
                alt=""
                width={390}
                height={296}
                className="aspect-[195/148] w-full rounded-3xl object-cover"
              />
              <span className="bg-secondary text-secondary-foreground absolute -bottom-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-1.5 text-sm whitespace-nowrap">
                {card.label}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 container">
        <motion.div
          className="text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <TextReveal mode="animate">
            <h2 className="mx-auto max-w-4xl text-4xl leading-none tracking-tighter md:text-6xl lg:text-7xl">
              Ready to capture together?
            </h2>
          </TextReveal>
          <TextReveal mode="animate" delay={REVEAL_STAGGER} className="mt-8">
            <p className="text-muted-foreground mx-auto max-w-lg text-base leading-relaxed">
              Download Keeps and start creating shared memories at your next
              event.
            </p>
          </TextReveal>
          <TextReveal
            mode="animate"
            delay={REVEAL_STAGGER * 2}
            className="mt-10"
          >
            <div className="flex flex-wrap items-center justify-center gap-3">
              <StoreBadge
                href={appStoreUrl("web_hero_cta")}
                ariaLabel={STORE_LINKS.appStore.label}
                icon={<IconApple className="text-background size-[22px]" />}
                topLine="Download on the"
                storeName="App Store"
                external
              />
              {SHOW_ANDROID && (
                <StoreBadge
                  href={STORE_LINKS.googlePlay.href}
                  ariaLabel={STORE_LINKS.googlePlay.label}
                  icon={
                    <IconGooglePlay className="text-background size-[22px]" />
                  }
                  topLine="Get it on"
                  storeName="Google Play"
                />
              )}
            </div>
          </TextReveal>
          <TextReveal
            mode="animate"
            delay={REVEAL_STAGGER * 3}
            className="mt-12"
          >
            <p className="font-handwritten text-text-tertiary text-2xl">
              Get started now
            </p>
          </TextReveal>
        </motion.div>
      </div>
    </section>
  );
}
